
import React, { useState } from 'react';
// TruTac fetch helper copied from RestCalculator
const fetchTruTacData = async (start, end) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    const requestBody = {
      recordsAfterDateTime: start.toISOString(),
      recordsBeforeDateTime: end.toISOString(),
      includeEmptyDates: true,
      includeDriversWithNoEntries: true
    };
    const response = await fetch("https://us-central1-weekend-availability-f7147.cloudfunctions.net/trutacProxy", {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText.substring(0, 100)}`);
    }
    const text = await response.text();
    if (!text) return { success: true, data: [] };
    try {
      const parsed = JSON.parse(text);
      const data = Array.isArray(parsed) ? parsed : (parsed.data || []);
      return { success: true, data: data };
    } catch (parseError) {
      throw new Error(`Invalid JSON response`);
    }
  } catch (e) {
    return { success: false, error: e.message };
  }
};

const formatDateKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const EmployeeDuty = ({ db, usersList }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedDriver, setSelectedDriver] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const fetchDutyData = async () => {
    setIsLoading(true);
    setError('');
    try {
      // TruTac expects a date range, so use the selected day for both start/end
      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(23, 59, 59, 999);
      const result = await fetchTruTacData(start, end);
      if (!result.success) {
        setError(result.error || 'Unable to load TruTac data.');
        setRows([]);
        setIsLoading(false);
        return;
      }
      // Map TruTac records to table rows, improve matching
      // TruTac returns an array of drivers, each with a .records array for shifts
      const rowsData = [];
      (result.data || []).forEach((driver) => {
        let initials = driver.driverInitials || driver.initials || '';
        let user = usersList.find((u) =>
          u.initials && initials && u.initials.toLowerCase() === initials.toLowerCase()
        );
        if (!user && driver.driverName) {
          user = usersList.find((u) =>
            u.displayName && driver.driverName && u.displayName.toLowerCase() === driver.driverName.toLowerCase()
          );
        }
        const name = user ? user.displayName : (driver.driverName || initials || 'UNKNOWN');
        initials = user ? user.initials : (initials || 'UNKNOWN');

        if (Array.isArray(driver.records)) {
          driver.records.forEach((rec) => {
            let recDate = null;
            if (rec.firstCardIn && rec.firstCardIn.date) {
              recDate = new Date(rec.firstCardIn.date);
            } else if (rec.manualEntryStart && rec.manualEntryStart.date) {
              recDate = new Date(rec.manualEntryStart.date);
            }
            if (!recDate) return;
            const isSameDay = recDate.getFullYear() === selectedDate.getFullYear() &&
              recDate.getMonth() === selectedDate.getMonth() &&
              recDate.getDate() === selectedDate.getDate();
            if (!isSameDay) return;

            let cardIn = '';
            let cardOut = '';
            let cardInDate = null;
            let cardOutDate = null;
            if (rec.firstCardIn && rec.firstCardIn.date) {
              cardInDate = new Date(rec.firstCardIn.date);
              cardIn = cardInDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (rec.manualEntryStart && rec.manualEntryStart.date) {
              cardInDate = new Date(rec.manualEntryStart.date);
              cardIn = cardInDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (rec.lastCardOut && rec.lastCardOut.date) {
              cardOutDate = new Date(rec.lastCardOut.date);
              cardOut = cardOutDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (rec.manualEntryEnd && rec.manualEntryEnd.date) {
              cardOutDate = new Date(rec.manualEntryEnd.date);
              cardOut = cardOutDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }

            // Analyze activities for Drive/Other/Rest only within cardIn/cardOut window
            let driveMins = 0, otherMins = 0, restMins = 0;
            if (cardInDate && cardOutDate && Array.isArray(rec.activities)) {
              rec.activities.forEach((a) => {
                if (!a.start || !a.end) return;
                const start = new Date(a.start);
                const end = new Date(a.end);
                // Only count activity if it falls within the shift window
                if (end <= cardInDate || start >= cardOutDate) return;
                const boundedStart = start < cardInDate ? cardInDate : start;
                const boundedEnd = end > cardOutDate ? cardOutDate : end;
                const mins = Math.max(0, (boundedEnd - boundedStart) / 60000);
                if (a.type === 'DRIVE') driveMins += mins;
                else if (a.type === 'OTHER_WORK') otherMins += mins;
                else if (a.type === 'REST') restMins += mins;
              });
            }
            // Fallback: if no activities, use total shift duration as Drive
            if ((driveMins + otherMins + restMins) === 0 && cardInDate && cardOutDate) {
              driveMins = Math.max(0, (cardOutDate - cardInDate) / 60000);
            }

            // Prefer manual entry times for display
            let displayStart = cardIn;
            let displayFinish = cardOut;
            if (rec.manualEntryStart && rec.manualEntryStart.date) {
              const manualStartDate = new Date(rec.manualEntryStart.date);
              displayStart = manualStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (rec.manualEntryEnd && rec.manualEntryEnd.date) {
              const manualEndDate = new Date(rec.manualEntryEnd.date);
              displayFinish = manualEndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            rowsData.push({
              initials,
              name,
              start: displayStart,
              finish: displayFinish,
              manualStart: rec.manualEntryStart && rec.manualEntryStart.date ? rec.manualEntryStart.date : null,
              manualEnd: rec.manualEntryEnd && rec.manualEntryEnd.date ? rec.manualEntryEnd.date : null,
              notes: rec.notes || '',
            });
          });
        }
      });
      setRows(rowsData);
    } catch (e) {
      setError('Unable to load TruTac data.');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered list of drivers for dropdown
  const driverOptions = rows
    .map((row) => ({ initials: row.initials, name: row.name }))
    .filter((v, i, arr) => arr.findIndex((x) => x.initials === v.initials) === i);

  const filteredRows = selectedDriver
    ? rows.filter((row) => row.initials === selectedDriver)
    : rows;

  return (
    <div className="card" style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <h3 style={{ marginBottom: '15px' }}>Employee Duty (Day)</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
        <label style={{ fontSize: '14px' }}>
          Date:
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
            style={{ marginLeft: '8px', padding: '6px' }}
          />
        </label>
        <button onClick={fetchDutyData} disabled={isLoading} style={{ background: 'var(--primary)', color: 'white', padding: '8px 14px', border: 'none', borderRadius: '6px' }}>
          {isLoading ? 'Loading...' : 'Load Day'}
        </button>
        <label style={{ fontSize: '14px', marginLeft: '20px' }}>
          Filter by Driver:
          <select
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            style={{ marginLeft: '8px', padding: '6px' }}
          >
            <option value="">All</option>
            {driverOptions.map((d) => (
              <option key={d.initials} value={d.initials}>{d.name} ({d.initials})</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p style={{ color: 'var(--danger)', marginBottom: '10px' }}>{error}</p>}
      {!isLoading && filteredRows.length === 0 && <p className="text-sm">No driver timesheet data found for this day. Select a different date and click Load Day.</p>}

      {filteredRows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
            <thead>
              <tr style={{ background: 'var(--light)' }}>
                <th style={{ padding: '10px', border: '1px solid #e5e7eb', minWidth: '140px', textAlign: 'left' }}>Driver</th>
                <th style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>Start</th>
                <th style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>Finish</th>
                <th style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>Total Hours Worked</th>
                <th style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.initials + row.start + row.finish} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px', border: '1px solid #e5e7eb', fontWeight: '600' }}>{row.name} ({row.initials})</td>
                  <td style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{row.start || '—'}</td>
                  <td style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{row.finish || '—'}</td>
                  <td style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{(() => {
                    // Prefer manualEntryStart/manualEntryEnd if available
                    if (row.manualStart && row.manualEnd) {
                      const startDate = new Date(row.manualStart);
                      const endDate = new Date(row.manualEnd);
                      let diff = (endDate - startDate) / (1000 * 60 * 60);
                      if (diff < 0) diff += 24;
                      return diff.toFixed(2);
                    }
                    // Fallback to card in/out
                    if (row.start && row.finish) {
                      const date = new Date();
                      const [startH, startM] = row.start.split(':');
                      const [finishH, finishM] = row.finish.split(':');
                      const cardIn = new Date(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(startH), parseInt(startM));
                      const cardOut = new Date(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(finishH), parseInt(finishM));
                      let diff = (cardOut - cardIn) / (1000 * 60 * 60);
                      if (diff < 0) diff += 24;
                      return diff.toFixed(2);
                    }
                    return '0.00';
                  })()}</td>
                  <td style={{ padding: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>{row.notes || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeDuty;
